import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn, CurrencyUtils, FormatUtils, PaletteUtils } from '@shared/utils';
import type { RecipientResponse } from '@shared/api';
import { ErrorMessage, Icon, Modal, NoData, SearchInput, Text } from '@shared/widgets';
import SwapIcon from '@assets/icons/swap.svg';

interface SelectRecipientProps {
  loading?: boolean;
  label: ReactNode;
  items: RecipientResponse[];
  error?: ReactNode;
  value?: RecipientResponse | null;
  onChange: (value?: RecipientResponse | null) => void;
  searchValue: string;
  onSearch: (value: string) => void;
}

export const SelectRecipient = (props: SelectRecipientProps) => {
  const [open, setOpen] = useState<boolean>(false);

  const { t } = useTranslation();

  const selectHandler = (value?: RecipientResponse) => {
    props.onChange(value);
    setOpen(false);
  };

  const title = useMemo(() => props.label ?? t('placeholders.selectRecipient'), [props.label]);
  return (
    <div>
      <Text className={cn('mb-3', PaletteUtils.isVcfcPalette && 'dark:text-white/50')}>{title}</Text>
      <AccountListItem
        value={props.value}
        loading={props.loading}
        isShowEndIcon
        onClick={() => {
          if (props.loading) return;
          setOpen(true);
        }}
      />
      <ErrorMessage text={props.error} />
      <Modal isOpen={open} onClose={() => setOpen(false)} title={t('selectRecipient.modals.addRecipients.title')} width='450px' position='top'>
        <SearchInput className='mb-2' withDebounce value={props.searchValue} onChange={props.onSearch} />
        <div className='flex flex-col gap-2 overflow-y-auto'>
          {props.items.map((item, idx) => (
            <AccountListItem
              key={idx}
              className={cn(PaletteUtils.isInsigniaPalette && 'dark:bg-slate', 'dark:hover:bg-slate/70')}
              value={item}
              isShowEndIcon={false}
              onClick={() => selectHandler(item)}
            />
          ))}
          {props.items.length === 0 && <NoData />}
        </div>
      </Modal>
    </div>
  );
};

interface AccountListItemProps {
  className?: string;
  loading?: boolean;
  value?: RecipientResponse | null;
  isShowEndIcon?: boolean;
  onClick: () => void;
}

const AccountListItem = (props: AccountListItemProps) => {
  const { t } = useTranslation();

  return (
    <div
      className={cn(
        'flex w-full cursor-pointer items-center gap-3 rounded-[2px] p-3 transition-colors duration-200',
        'max-sm:flex-wrap',
        PaletteUtils.isInsigniaPalette && ['bg-ice dark:bg-eerie-black', 'hover:bg-ghost-white dark:hover:bg-transport-black'],
        PaletteUtils.isVcfcPalette && ['bg-ghost-white dark:bg-light/5', 'hover:bg-gainsborough dark:hover:bg-light/20'],
        props.loading && 'pointer-events-none',
        props.className,
      )}
      onClick={props.onClick}
    >
      <div className={cn('flex w-full cursor-pointer bg-transparent', props.isShowEndIcon && 'w-auto')}>
        {props.value === undefined && (
          <div className='flex items-center gap-2'>
            <Text
              className={cn(
                'flex h-11 w-11 shrink-0 items-center justify-center rounded-full border-2 border-dashed border-primary',
                PaletteUtils.isVcfcPalette && 'dark:border-white',
              )}
              weight='semibold'
              size='3xl'
              color='primary'
            >
              +
            </Text>
            <Text weight='semibold'>{props.loading ? 'Loading...' : t('placeholders.selectRecipient')}</Text>
          </div>
        )}
        {props.value && (
          <div className='item-center flex items-center gap-2'>
            <Text className='flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary' weight='semibold' color='white'>
              {FormatUtils.getRecipientInitials(props.value)}
            </Text>
            <ul className='flex flex-col'>
              <Text tag='li' weight='semibold'>
                {FormatUtils.getRecipientName(props.value)}
              </Text>
              <Text tag='li' weight='semibold' size='xs' color='gray'>
                {props.value.accountNumber || props.value.iban || '-'}
              </Text>
            </ul>
          </div>
        )}
      </div>
      {props.value?.currency && (
        <div className='flex items-center justify-center gap-1 rounded-[5px] border border-primary p-2'>
          <Text weight='semibold' size='xs' color='primary'>
            {CurrencyUtils.getSymbolByCode(props.value.currency)}
          </Text>
          <Text uppercase weight='semibold' size='xs' color='primary'>
            {props.value.currency}
          </Text>
        </div>
      )}
      {props.isShowEndIcon && (
        <Icon className='rotate-90' icon={<SwapIcon width='24' height='24' className={cn(PaletteUtils.isVcfcPalette && 'text-pearl-dark-gray')} />} />
      )}
    </div>
  );
};
